import axios from "axios";
import dayjs from "dayjs";

export const MANGO_TRANSIT_COST_KEY = "mangoTransitCost";
export const MANGO_SHIPMENT_READY_KEY = "mangoShipmentReady";
export const PRICING_DRAFT_KEY = "sundorbonPricingDraft";

const DEFAULT_CARGO_TYPE_ID = 1;

export function readMangoPricingDraft() {
  try {
    const raw = localStorage.getItem(PRICING_DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    return draft?.pricingType === "mango" ? draft : null;
  } catch {
    return null;
  }
}

export function isMangoShipmentReady() {
  return sessionStorage.getItem(MANGO_SHIPMENT_READY_KEY) === "1";
}

export function markMangoShipmentReady() {
  sessionStorage.setItem(MANGO_SHIPMENT_READY_KEY, "1");
}

export function clearMangoShipmentSession() {
  sessionStorage.removeItem(MANGO_TRANSIT_COST_KEY);
  sessionStorage.removeItem(MANGO_SHIPMENT_READY_KEY);
}

export function saveMangoTransitCost(results) {
  sessionStorage.setItem(MANGO_TRANSIT_COST_KEY, JSON.stringify(results));
}

export function loadMangoTransitCost() {
  try {
    const raw = sessionStorage.getItem(MANGO_TRANSIT_COST_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function buildMangoProducDetails(draft, fruitsWeights = [], dimensions = []) {
  const weightId = parseInt(draft?.mango?.weightId, 10);
  const weight = fruitsWeights.find((w) => w.id === weightId);
  const dimId = weight?.dimensionId ?? draft?.mango?.dimensionId;
  const dim = dimensions.find((d) => d.id === parseInt(dimId, 10));
  const parcel = draft?.parcel || {};
  const parsedQty = parseInt(draft?.mango?.qty, 10);
  const mangoQty = Math.min(5, Math.max(1, Number.isFinite(parsedQty) ? parsedQty : 1));

  const length = dim?.length ?? parcel.length ?? "";
  const width = dim?.width ?? parcel.width ?? "";
  const height = dim?.height ?? parcel.height ?? "";
  const weightVal =
    weight?.minimumWeight ?? weight?.maximumWeight ?? parcel.weight ?? "";

  return [
    {
      productGroup: 19,
      productType: 18,
      productQty: mangoQty,
      itemLength: length !== "" && length != null ? String(length) : "",
      itemWidth: width !== "" && width != null ? String(width) : "",
      itemHeight: height !== "" && height != null ? String(height) : "",
      itemWeight:
        weightVal !== "" && weightVal != null ? String(weightVal) : "",
      itemPackaging: "No",
    },
  ];
}

export function mergeReceiverIntoDraft(draft, receiver, additionalInfo) {
  return {
    ...draft,
    toPostCode: (receiver.receiverPostCode || draft.toPostCode || "").toUpperCase(),
    receiverName: receiver.receiverName || "",
    receiverEmail: receiver.receiverEmail || "",
    receiverMobileNumber: receiver.receiverMobileNumber || "",
    receiverCountry: receiver.receiverCountry || "",
    receiverCity: receiver.receiverCity || "",
    receiverLandmark: receiver.receiverLandmark || "",
    receiverDoorNo: receiver.receiverDoorNo || "",
    receiverStreetName: receiver.receiverStreetName || "",
    receiverRemarks: receiver.receiverRemarks || "",
    receiverAddress: receiver.receiverAddress || "",
    receiverPostCode: (receiver.receiverPostCode || "").toUpperCase(),
    additionalInfo: additionalInfo || "",
    mangoSetupComplete: true,
  };
}

export async function fetchMangoTransitionCost(routeId, producDetails) {
  const apiAddress = process.env.REACT_APP_API_URL;
  const today = dayjs().format("YYYY-MM-DD");
  const routingTypeId = parseInt(routeId, 10);

  const transitionCostRequests = producDetails
    .filter((item) => {
      const itemTypeId = parseInt(item.productType, 10);
      const itemCategoryId = parseInt(item.productGroup, 10);
      return (
        !isNaN(itemTypeId) &&
        !isNaN(itemCategoryId) &&
        itemTypeId > 0 &&
        itemCategoryId > 0
      );
    })
    .map((item) => ({
      routingTypeId,
      parcelLength: parseFloat(item.itemLength) || 0,
      parcelWidth: parseFloat(item.itemWidth) || 0,
      parcelHeight: parseFloat(item.itemHeight) || 0,
      parcelWeight: parseFloat(item.itemWeight) || 0,
      cargoTypeId: DEFAULT_CARGO_TYPE_ID,
      isExtraPackaging: false,
      isPickUp: false,
      itemTypeId: parseInt(item.productType, 10),
      itemCategoryId: parseInt(item.productGroup, 10),
      shipmentArrivalDate: today,
      productQty: parseInt(item.productQty, 10) || 0,
    }));

  if (transitionCostRequests.length === 0) {
    throw new Error("INCOMPLETE_PARCEL");
  }

  const response = await axios.post(
    `${apiAddress}TransitionCost/GetTotalTransitionCost`,
    transitionCostRequests,
    { headers: { "Content-Type": "application/json" } },
  );

  return Array.isArray(response.data) ? response.data : [response.data];
}

export function summarizeTransitCostResults(transitionCostResults) {
  const rowAmount = (result) =>
    parseFloat(
      result?.totalCost ??
        result?.totalCostWithoutVAT ??
        result?.totalCostWithVAT ??
        0,
    ) || 0;

  const totalCost = transitionCostResults.reduce(
    (sum, result) => sum + rowAmount(result),
    0,
  );
  const totalPickUpCost = transitionCostResults.reduce(
    (sum, result) => sum + result.pickUpCost,
    0,
  );
  const totalRouteCost = transitionCostResults.reduce(
    (sum, result) => sum + result.routeCost,
    0,
  );
  const totalItemCategoryCost = transitionCostResults.reduce(
    (sum, result) => sum + result.itemCategoryCost,
    0,
  );
  const totalItemTypeCost = transitionCostResults.reduce(
    (sum, result) => sum + result.itemTypeCost,
    0,
  );
  const totalWeightCost = transitionCostResults.reduce(
    (sum, result) => sum + result.weightCost,
    0,
  );
  const totalDimensionCost = transitionCostResults.reduce(
    (sum, result) => sum + result.dimensionCost,
    0,
  );
  const totalExtraPackagingCost = transitionCostResults.reduce(
    (sum, result) => sum + result.extraPackagingCost,
    0,
  );
  const totalCargoCost = transitionCostResults.reduce(
    (sum, result) => sum + result.cargoCost,
    0,
  );
  const vatPercentageAmount = transitionCostResults.reduce(
    (sum, result) => sum + result.vatPercentageAmount,
    0,
  );

  return {
    transitionCostResults,
    totalCost,
    totalPickUpCost,
    totalRouteCost,
    totalItemCategoryCost,
    totalItemTypeCost,
    totalWeightCost,
    totalDimensionCost,
    totalExtraPackagingCost,
    totalCargoCost,
    vatPercentageAmount,
  };
}
